import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, ArrowRight, ShieldCheck, MapPin, FileText } from "lucide-react";
import { useMeta, injectJsonLd, removeJsonLd } from "../lib/seo";
import { track } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { PRICE_COPY } from "../config/pricing";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const FAQS = [
  { q: "How does Mail-it-for-me work?", a: "After your appeal letter is ready, we print it and mail it by USPS Certified Mail with tracking — either bundled in the Letter + Certified Mail tier or added to any purchased letter. The tracking number saves to your account with a direct USPS.com tracking link. When USPS shows it delivered, confirm it on your appeal page — that starts the response check-ins." },
  { q: "What mailing options are there?", a: "One: USPS Certified Mail with tracking and return receipt included — the only class that produces the proof of delivery (including the recipient's signature) an appeal actually needs, so it's the only one we offer, with nothing extra to decide or pay for at the send step. If your contract's notice or arbitration clause specifically requires return receipt, we'll flag the section when we see it." },
  { q: "Does mailing my letter affect whether my appeal succeeds?", a: "No. Mailing is a delivery service only — it gets your letter there with proof of delivery. It does not change the merits of your appeal, and no outcome is guaranteed." },
  { q: "What if my document needs a handwritten signature?", a: "A few documents need a handwritten (ink) signature. We'll tell you when that's the case — those you print, sign, and mail yourself." },
  { q: "Is there a page limit for mailed packages?", a: "Yes — mailed packages (letter plus any exhibits, letter size, printed double-sided so 12 pages = 6 sheets) are limited to 12 pages. If your package runs longer, trim exhibits, use the 2-per-page photo layout, include only the diagnosis page of a long technician report, or download the full package and mail it yourself." },
];

export default function MailMyAppeal() {
  const navigate = useNavigate();
  useMeta("Send Your Appeal by USPS Certified Mail | Denial Decoder",
    `Already have your letter? We print it and send it by USPS Certified Mail with tracking for ${PRICE_COPY.mailStandalone}, so you have a dated record of mailing and delivery.`);

  useEffect(() => {
    track("mail_promo_viewed", { location: "mail_landing" });
    injectJsonLd("mail-faq-schema", {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
    return () => removeJsonLd("mail-faq-schema");
  }, []);

  return (
    <div className="fade-in" data-testid="mail-landing-page">
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300 mb-4">Mail-it-for-me</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1]">We write it, and we can mail it — certified, with tracking.</h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed">Never print, buy an envelope, or find a post office. Denial Decoder prints your appeal letter and mails it through USPS — and the delivery date starts your deadline clock automatically.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button size="lg" onClick={() => { track("start_check", { claimType: "home-warranty" }); navigate("/check"); }} className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8" data-testid="mail-landing-cta">
                Decode my denial — free <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/pricing")} className="rounded-full px-8 bg-white/10 text-white border-white/30 hover:bg-white/20" data-testid="mail-landing-pricing-cta">
                See mailing options
              </Button>
            </div>
          </div>
          <img src="/images/mail-envelope.webp" width="576" height="576"
            alt="Envelope with a tracking badge" className="w-56 sm:w-72 rounded-3xl shadow-2xl" data-testid="mail-landing-illustration" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: FileText, t: "Your letter, print-ready", d: "Your appeal letter — written from your contract and denial letter — is formatted and printed for you." },
            { icon: Truck, t: "Mailed through USPS", d: "One option: Certified Mail with tracking — the only class that produces proof of delivery. Your USPS tracking number links straight to USPS.com." },
            { icon: MapPin, t: "Tracking saved, reminders set", d: "Your tracking number is saved to your appeal page. Confirm delivery from your USPS lookup, and we email you at 14, 25 and 35 days to check whether the company responded." },
          ].map((s) => (
            <div key={s.t} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`mail-how-${s.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <span className="bg-emerald-50 text-emerald-700 p-3 rounded-xl inline-block"><s.icon size={20} /></span>
              <h2 className="text-lg font-semibold mt-4">{s.t}</h2>
              <p className="text-sm text-slate-600 mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">One mail option. One price.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 max-w-xl mx-auto">
            {[[PRICE_COPY.letterPlusMail, "Bundled with your letter", "Letter + Certified Mail tier — recommended at checkout"], [PRICE_COPY.mailStandalone, "Added later", "Certified mailing for a letter you already purchased"]].map(([price, label, sub]) => (
              <div key={label} className="border border-slate-200 rounded-xl p-5" data-testid={`mail-landing-option-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="text-2xl font-extrabold">{price}</p>
                <p className="text-sm font-semibold text-slate-700 mt-1">{label}</p>
                <p className="text-xs text-slate-500 mt-1">{sub}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-5">USPS Certified Mail with tracking and return receipt (recipient&rsquo;s signature) included at no extra charge. Mailing is a delivery service — it doesn't affect the merits of your appeal.</p>
          <p className="text-xs text-slate-500 mt-2" data-testid="mail-landing-page-limit">Mailed packages are limited to 12 pages (letter + exhibits, printed double-sided — 6 sheets, letter size).</p>
          <p className="text-xs text-slate-500 mt-2 flex items-center justify-center gap-1"><ShieldCheck size={13} /> A few documents need a handwritten signature — we'll tell you when, and you mail those yourself.</p>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-4 py-16" data-testid="mail-landing-faq">
        <h2 className="text-2xl font-bold mb-6 text-center">Mailing FAQ</h2>
        <Accordion type="single" collapsible>
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q.slice(0, 30)} value={`mf-${i}`}>
              <AccordionTrigger data-testid={`mail-faq-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent>{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="text-center mt-10">
          <Button size="lg" onClick={() => { track("start_check", { claimType: "home-warranty" }); navigate("/check"); }} className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-8" data-testid="mail-landing-bottom-cta">
            Start my free denial check <ArrowRight className="ml-2" size={18} />
          </Button>
          <p className="text-xs text-slate-500 mt-3">Free preview first. Mailing is optional, always.</p>
        </div>
      </section>
    </div>
  );
}
